import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Eye, EyeOff, Loader2, User, Phone, MapPin } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent } from "../components/ui/card";
import { useAuth } from "../App";
import apiClient from "../lib/api";
import { toastApiError } from "../lib/toastApiError";
import { FormPageShell } from "../components/FormPageShell";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_97109381-3560-4331-a0e4-28136ead7f0e/artifacts/z6ijff3c_logo1.png";

const ClientRegisterPage = () => {
  const navigate = useNavigate();
  const { setUser } = useAuth();
  const { t } = useTranslation("auth");
  const { t: tc } = useTranslation("common");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    city: "",
    email: "",
    password: "",
    confirmPassword: ""
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.password) {
      toast.error(t("fillRequiredFields"));
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error(t("passwordsMismatch"));
      return;
    }

    if (formData.password.length < 6) {
      toast.error(t("passwordMinLength"));
      return;
    }

    setLoading(true);
    try {
      const response = await apiClient.post("/auth/register/client", {
        name: formData.name,
        phone: formData.phone || null,
        city: formData.city || null,
        email: formData.email,
        password: formData.password
      });

      localStorage.setItem("omu_token", response.data.access_token);
      setUser(response.data.user);

      toast.success(t("clientRegisterSuccess"));
      navigate("/");
    } catch (error) {
      toastApiError(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormPageShell centered={false}>
      <div className="max-w-md mx-auto">
        <div className="text-center mb-8">
          <Link to="/">
            <img src={LOGO_URL} alt="OMU" className="h-20 w-auto mx-auto" />
          </Link>
          <h1 className="font-heading text-2xl font-bold text-slate-900 mt-4">
            {t("registerClientTitle")}
          </h1>
          <p className="text-slate-500 mt-2">
            {t("registerClientSubtitle")}
          </p>
        </div>

        <Card className="border-0 shadow-soft">
          <CardContent className="p-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">{t("fullName")}</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <Input
                    id="name"
                    name="name"
                    type="text"
                    placeholder={t("fullNamePlaceholder")}
                    value={formData.name}
                    onChange={handleInputChange}
                    className="h-12 rounded-xl pl-10"
                    data-testid="client-register-name-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone">{t("phoneOptional")}</Label>
                <div className="relative">
                  <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <Input
                    id="phone"
                    name="phone"
                    type="tel"
                    placeholder={t("phonePlaceholder")}
                    value={formData.phone}
                    onChange={handleInputChange}
                    className="h-12 rounded-xl pl-10"
                    data-testid="client-register-phone-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="city">{t("cityOptional")}</Label>
                <div className="relative">
                  <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-400" />
                  <Input
                    id="city"
                    name="city"
                    type="text"
                    placeholder={t("cityPlaceholder")}
                    value={formData.city}
                    onChange={handleInputChange}
                    className="h-12 rounded-xl pl-10"
                    data-testid="client-register-city-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">{t("email")} *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder={t("emailPlaceholder")}
                  value={formData.email}
                  onChange={handleInputChange}
                  className="h-12 rounded-xl"
                  data-testid="client-register-email-input"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="password">{t("password")} *</Label>
                <div className="relative">
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    placeholder={t("passwordPlaceholder")}
                    value={formData.password}
                    onChange={handleInputChange}
                    className="h-12 rounded-xl pr-12"
                    data-testid="client-register-password-input"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="absolute right-2 top-1/2 -translate-y-1/2"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="confirmPassword">{t("confirmPassword")} *</Label>
                <Input
                  id="confirmPassword"
                  name="confirmPassword"
                  type="password"
                  placeholder="••••••••"
                  value={formData.confirmPassword}
                  onChange={handleInputChange}
                  className="h-12 rounded-xl"
                  data-testid="client-register-confirm-password-input"
                />
              </div>

              <Button
                type="submit"
                className="w-full h-12 rounded-full bg-omu-blue-500 hover:bg-omu-blue-600"
                disabled={loading}
                data-testid="client-register-submit-btn"
              >
                {loading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  t("createMyAccount")
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="text-center text-sm text-slate-500 mt-6">
          {t("alreadySignedIn")}{" "}
          <Link to="/login" className="text-omu-blue-500 font-medium hover:underline">
            {t("signIn")}
          </Link>
        </p>

        <p className="text-center text-sm text-slate-500 mt-2">
          {t("doctorPrompt")}{" "}
          <Link to="/register/doctor" className="text-omu-blue-500 font-medium hover:underline">
            {t("doctorRegister")}
          </Link>
        </p>

        <p className="text-center text-sm text-slate-500 mt-2">
          {t("proPrompt")}{" "}
          <Link to="/register" className="text-omu-blue-500 font-medium hover:underline">
            {t("proRegister")}
          </Link>
        </p>

        <p className="text-center text-sm text-slate-400 mt-4">
          <Link to="/" className="hover:text-slate-600">
            {tc("backHome")}
          </Link>
        </p>
      </div>
    </FormPageShell>
  );
};

export default ClientRegisterPage;
